import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert, AlertIcon, Box, Button, Flex, FormControl, FormHelperText, FormLabel, HStack, Icon, Input,
    Select, SimpleGrid, Text, Textarea, VStack, useToast,
} from '@chakra-ui/react';
import { FaMapMarkerAlt, FaStore, FaUser } from 'react-icons/fa';
import SeletorEnderecoMapa from '../SeletorEnderecoMapa';
import { doCadastro, impedimentos as impedimentosDoEndereco, paraPayload } from '../SeletorEnderecoMapa/endereco';
import { formatarTelefone, soDigitosTelefone, telefoneValido } from '../ModalSolicitacaoLalamove/telefone';
import { salvarPontoColetaLalamove } from '../../services/api';

/**
 * Endereco de coleta de um fornecedor (#2307).
 *
 * O mesmo endereco serve as duas telas de compras: liga o fornecedor das
 * listas de compras e o dos furos, que sao cadastros separados no sistema.
 * O endereco usa o mesmo seletor e as mesmas travas da entrega da venda:
 * numero, UF, ponto no mapa conferido e distancia da loja.
 */
export default function FormEnderecoColeta({
    ponto = null,
    fornecedores = { listas: [], furos: [] },
    fornecedorFixo = null,
    compacto = false,
    onSalvo,
    onCancelar,
}) {
    const toast = useToast();
    const [salvando, setSalvando] = useState(false);
    const [erro, setErro] = useState(null);

    const [form, setForm] = useState(() => ({
        nome: ponto?.nome || fornecedorFixo?.nome || '',
        fornecedor_id: ponto?.fornecedor_id ?? (fornecedorFixo?.tipo === 'lista' ? fornecedorFixo.id : '') ?? '',
        cotacao_fornecedor_id: ponto?.cotacao_fornecedor_id ?? (fornecedorFixo?.tipo === 'furo' ? fornecedorFixo.id : '') ?? '',
        contato_nome: ponto?.contato_nome || '',
        contato_telefone: soDigitosTelefone(ponto?.contato_telefone || ''),
        instrucoes: ponto?.instrucoes || '',
    }));

    // Endereco ja cadastrado foi conferido no mapa quando foi salvo.
    const [endereco, setEndereco] = useState(() => (ponto?.endereco
        ? { ...doCadastro(ponto.endereco), pontoConfirmado: true }
        : doCadastro({})));

    useEffect(() => { setErro(null); }, [form, endereco]);

    const alterar = (campo, valor) => setForm((f) => ({ ...f, [campo]: valor }));

    const problemasEndereco = useMemo(() => impedimentosDoEndereco(endereco, null), [endereco]);
    const semFornecedor = !form.fornecedor_id && !form.cotacao_fornecedor_id;
    const pendencias = [
        !form.nome.trim() && 'Informe o nome do fornecedor.',
        semFornecedor && 'Ligue o endereço a pelo menos um fornecedor.',
        !telefoneValido(form.contato_telefone) && 'Informe o telefone do fornecedor com DDD: a Lalamove exige.',
        ...problemasEndereco,
    ].filter(Boolean);

    // Fornecedor que ja tem endereco em outro cadastro nao pode ser escolhido de novo.
    const ocupado = (f) => f.ponto_coleta_id && f.ponto_coleta_id !== ponto?.id;

    const salvar = async () => {
        if (pendencias.length > 0) {
            toast({ title: 'Confira o cadastro', description: pendencias[0], status: 'warning', duration: 7000, isClosable: true });
            return;
        }

        setSalvando(true);
        setErro(null);
        try {
            const res = await salvarPontoColetaLalamove(ponto?.id || null, {
                nome: form.nome.trim(),
                fornecedor_id: form.fornecedor_id ? Number(form.fornecedor_id) : null,
                cotacao_fornecedor_id: form.cotacao_fornecedor_id ? Number(form.cotacao_fornecedor_id) : null,
                contato_nome: form.contato_nome.trim() || null,
                contato_telefone: form.contato_telefone,
                instrucoes: form.instrucoes.trim() || null,
                origem: paraPayload(endereco),
            });
            toast({
                title: ponto ? 'Endereço de coleta atualizado' : 'Endereço de coleta cadastrado',
                description: res.ponto?.nome,
                status: 'success',
                isClosable: true,
            });
            if (onSalvo) onSalvo(res.ponto);
        } catch (e) {
            const d = e.response?.data || {};
            const msg = d.error || (d.errors ? Object.values(d.errors).flat()[0] : null) || d.message || 'Não foi possível salvar o endereço.';
            setErro(msg);
            toast({ title: 'Não foi possível salvar', description: msg, status: 'error', duration: 9000, isClosable: true });
        } finally {
            setSalvando(false);
        }
    };

    const rotulo = (texto) => (
        <FormLabel fontSize="xs" color="gray.500" fontWeight="bold" textTransform="uppercase" mb={1}>{texto}</FormLabel>
    );

    return (
        <VStack align="stretch" spacing={5}>
            <Box>
                <HStack spacing={2} mb={3}>
                    <Icon as={FaStore} color="orange.500" boxSize={3} />
                    <Text fontSize="sm" fontWeight="bold" color="gray.700">Fornecedor</Text>
                </HStack>
                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
                    <FormControl isRequired>
                        {rotulo('Nome do fornecedor')}
                        <Input size="sm" borderRadius="lg" value={form.nome} maxLength={120}
                            onChange={(e) => alterar('nome', e.target.value)} placeholder="Ex.: MSAM" />
                    </FormControl>
                    <FormControl>
                        {rotulo('Nas listas de compras')}
                        <Select size="sm" borderRadius="lg" placeholder="não vende por lista" value={form.fornecedor_id || ''}
                            onChange={(e) => alterar('fornecedor_id', e.target.value)}
                            isDisabled={fornecedorFixo?.tipo === 'lista'}>
                            {(fornecedores.listas || []).map((f) => (
                                <option key={f.id} value={f.id} disabled={ocupado(f)}>
                                    {f.nome}{ocupado(f) ? ' (já tem endereço)' : ''}
                                </option>
                            ))}
                            {fornecedorFixo?.tipo === 'lista' && !(fornecedores.listas || []).some((f) => f.id === fornecedorFixo.id) && (
                                <option value={fornecedorFixo.id}>{fornecedorFixo.nome}</option>
                            )}
                        </Select>
                    </FormControl>
                    <FormControl>
                        {rotulo('Na cobertura de furos')}
                        <Select size="sm" borderRadius="lg" placeholder="não atende furo" value={form.cotacao_fornecedor_id || ''}
                            onChange={(e) => alterar('cotacao_fornecedor_id', e.target.value)}
                            isDisabled={fornecedorFixo?.tipo === 'furo'}>
                            {(fornecedores.furos || []).map((f) => (
                                <option key={f.id} value={f.id} disabled={ocupado(f)}>
                                    {f.nome}{ocupado(f) ? ' (já tem endereço)' : ''}{f.ativo === false ? ' (inativo)' : ''}
                                </option>
                            ))}
                            {fornecedorFixo?.tipo === 'furo' && !(fornecedores.furos || []).some((f) => f.id === fornecedorFixo.id) && (
                                <option value={fornecedorFixo.id}>{fornecedorFixo.nome}</option>
                            )}
                        </Select>
                    </FormControl>
                </SimpleGrid>
                <Text fontSize="xs" color="gray.400" mt={2}>
                    A mesma empresa costuma aparecer nos dois cadastros (ex.: MSAM nas listas e MsamPatrick nos furos).
                    Ligue os dois para pedir uma corrida só com pedidos das duas telas.
                </Text>
            </Box>

            <Box>
                <HStack spacing={2} mb={3}>
                    <Icon as={FaUser} color="orange.500" boxSize={3} />
                    <Text fontSize="sm" fontWeight="bold" color="gray.700">Quem entrega a mercadoria ao motorista</Text>
                </HStack>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                    <FormControl>
                        {rotulo('Contato')}
                        <Input size="sm" borderRadius="lg" value={form.contato_nome} maxLength={60}
                            onChange={(e) => alterar('contato_nome', e.target.value)} placeholder="Ex.: Patrick" />
                    </FormControl>
                    <FormControl isRequired isInvalid={form.contato_telefone.length > 0 && !telefoneValido(form.contato_telefone)}>
                        {rotulo('Telefone com DDD')}
                        <Input size="sm" borderRadius="lg" inputMode="tel" maxLength={15}
                            value={formatarTelefone(form.contato_telefone)}
                            onChange={(e) => alterar('contato_telefone', soDigitosTelefone(e.target.value))}
                            placeholder="(11) 98888-7777" />
                        <FormHelperText fontSize="xs">A Lalamove passa este número para o motorista.</FormHelperText>
                    </FormControl>
                    <FormControl gridColumn={{ md: 'span 2' }}>
                        {rotulo('Instrução padrão para o motorista (opcional)')}
                        <Textarea size="sm" borderRadius="lg" rows={2} maxLength={500} value={form.instrucoes}
                            onChange={(e) => alterar('instrucoes', e.target.value)}
                            placeholder="Ex.: retirar no balcão dos fundos, falar com o estoque" />
                        <FormHelperText fontSize="xs">Entra sozinha no texto de toda corrida deste fornecedor.</FormHelperText>
                    </FormControl>
                </SimpleGrid>
            </Box>

            <Box>
                <HStack spacing={2} mb={2}>
                    <Icon as={FaMapMarkerAlt} color="orange.500" boxSize={3} />
                    <Text fontSize="sm" fontWeight="bold" color="gray.700">Endereço de coleta</Text>
                </HStack>
                <SeletorEnderecoMapa
                    aberto
                    compacto={compacto}
                    alturaMapa={compacto ? 200 : 240}
                    destinoInicial={endereco}
                    cadastro={null}
                    onChange={setEndereco}
                />
            </Box>

            {erro && (
                <Alert status="error" borderRadius="md" fontSize="sm"><AlertIcon />{erro}</Alert>
            )}

            <Flex justify="space-between" align="center" gap={2} wrap="wrap" borderTopWidth="1px" pt={3}>
                <Text fontSize="xs" color={pendencias.length ? 'orange.600' : 'green.600'} flex={1} minW="200px">
                    {pendencias.length ? pendencias[0] : 'Cadastro conferido'}
                </Text>
                <HStack spacing={2}>
                    {onCancelar && <Button size="sm" variant="ghost" onClick={onCancelar} isDisabled={salvando}>Cancelar</Button>}
                    <Button size="sm" colorScheme="purple" onClick={salvar} isLoading={salvando} loadingText="Salvando"
                        isDisabled={pendencias.length > 0} minH="36px">
                        {ponto ? 'Salvar alterações' : 'Cadastrar endereço'}
                    </Button>
                </HStack>
            </Flex>
        </VStack>
    );
}
